import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { db, getUser } from '@/firebaseApp';
import { v4 as uuidv4 } from 'uuid';
import { IClass } from '@/models/Classes';
import { IRefModule } from '@/models/Modules';
import Teacher from '@/services/Teacher';

class Classroom {
  async createClassRoom(name: string) {
    const user = getUser();
    if (!user) {
      return null;
    }

    const teacher = await Teacher.getLoggedInTeacher();
    if (!teacher) {
      return;
    }

    const uuid = uuidv4();
    const newClass = {
      name: name,
      modules: [],
      teacher: {
        uid: user.uid,
        email: user.email,
      },
    };

    // Add new class to teacher (to save the classroom order in the sidemenu)
    await db
      .collection('teachers')
      .doc(teacher.id)
      .update({
        classrooms: firebase.firestore.FieldValue.arrayUnion(uuid),
      });

    // Add new class to db
    await db
      .collection('classrooms')
      .doc(uuid)
      .set(newClass);

    return uuid;
  }

  async deleteClassroom(id: string) {
    const teacher = await Teacher.getLoggedInTeacher();
    if (!teacher) {
      return;
    }

    // Remove class from teacher (to update the classroom order in the sidemenu)
    await db
      .collection('teachers')
      .doc(teacher.id)
      .update({
        classrooms: firebase.firestore.FieldValue.arrayRemove(id),
      });

    await db
      .collection('classrooms')
      .doc(id)
      .delete();
  }

  async getClassroomById(id: string) {
    const doc = await db
      .collection('classrooms')
      .doc(id)
      .get();

    return doc.exists ? doc.data() : null;
  }

  async updateClassRoomModuleOrder(id: string, moduleRefs: IRefModule[]) {
    const user = getUser();
    if (!user) {
      return null;
    }

    return db
      .collection('classrooms')
      .doc(id)
      .set({ modules: moduleRefs }, { merge: true });
  }

  async getClassRooms() {
    const user = getUser();
    if (!user) {
      return [];
    }

    const snapshot = await db
      .collection('classrooms')
      .where('teacher.uid', '==', user.uid)
      .get();

    const classrooms: IClass[] = (!snapshot.empty
      ? snapshot.docs.map(doc => {
          return { uuid: doc.id, ...doc.data() };
        })
      : []) as IClass[];

    classrooms.sort((a, b) => {
      const keyA = a.name.toLowerCase();
      const keyB = b.name.toLowerCase();
      if (keyA > keyB) {
        return 1;
      } else if (keyA < keyB) {
        return -1;
      } else {
        return 0;
      }
    });

    return classrooms;
  }

  getClassroomsObserver() {
    const user = getUser();
    if (!user) {
      return null;
    }

    return db.collection('classrooms').where('teacher.uid', '==', user.uid);
  }

  getClassroomObserver(id: string) {
    return db.collection('classrooms').doc(id);
  }
}

export default new Classroom();
